// src/components/modals/CreateGroupModal.jsx
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import ChatAvatar from '../ChatAvatar';
import { getDisplayName } from '../../utils/userHelpers';
import { isValidAvatarFile, getAvatarErrorMessage } from '../../utils/fileHelpers';
import {
    ALLOWED_AVATAR_EXTENSIONS,
    ALLOWED_AVATAR_MIME_TYPES,
    MAX_AVATAR_SIZE
} from '../../utils/constants';
import './GroupModal.css';

const CreateGroupModal = ({
    isOpen,
    onClose,    
    onCreate,
    showNotification,                    // (groupData) => Promise
    searchUsers,                 // (query) => Promise<users[]>
}) => {
    const [groupName, setGroupName] = useState('');
    const [groupAvatarFile, setGroupAvatarFile] = useState(null);
    const [groupAvatarPreview, setGroupAvatarPreview] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(false);

    // Сброс при открытии
    useEffect(() => {
        if (isOpen) {
            setGroupName('');
            setGroupAvatarFile(null);
            setGroupAvatarPreview('');
            setSelectedMembers([]);
            setSearchQuery('');
            setSearchResults([]);
        }
    }, [isOpen]);

    // Debounce поиска
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const users = await searchUsers(searchQuery);
                // Фильтруем уже выбранных
                const filtered = users.filter(
                    u => !selectedMembers.some(m => m.id === u.id)
                );
                setSearchResults(filtered);
            } catch (error) {
                console.error('Search failed:', error);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [searchQuery, selectedMembers, searchUsers]);

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
       if (!isValidAvatarFile(file)) {
            showNotification(getAvatarErrorMessage(file), 'warning');
            e.target.value = '';   // сбрасываем выбор
            return;
        }
        setGroupAvatarFile(file);
        setGroupAvatarPreview(URL.createObjectURL(file));
        
    };

    const addMember = (user) => {
        setSelectedMembers([...selectedMembers, user]);
        /* setSearchQuery('');
        setSearchResults([]); */
    };

    const removeMember = (userId) => {
        setSelectedMembers(selectedMembers.filter(m => m.id !== userId));
    };

    const handleCreate = async () => {
        console.log("GroupName", groupName.trim());
        if (!groupName.trim()) {
           showNotification('Введите название группы','error');
            return;
        }

        setLoading(true);
        try {
            await onCreate({
                title: groupName,
                memberIds: selectedMembers.map(m => m.id),
                avatarFile: groupAvatarFile,
            });
            onClose();
        } catch (error) {
            console.error('Failed to create group:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Создать группу"
            icon="bi bi-people-fill"
            size="medium"
            footer={
                <>
                    <button className="btn-cancel" onClick={onClose}>Отмена</button>
                    <button 
                        className="btn-save" 
                        onClick={handleCreate}
                        disabled={loading}
                    >
                        {loading ? 'Создание...' : 'Создать'}
                    </button>
                </>
            }
        >
            <div className="group-avatar-section">
                {groupAvatarPreview ? (
                    <div className="avatar-preview">
                        <img src={groupAvatarPreview} alt="Avatar" className="avatar-img" />
                    </div>
                ) : (
                    <div className="avatar-preview">
                        <div className="avatar-placeholder">
                            <i className="bi bi-people-fill"></i>
                        </div>
                    </div>
                )}
                <label className="avatar-upload-btn">
                    <i className="bi bi-camera"></i> Загрузить логотип группы
                    <input name='ava'
                        type="file"
                        accept={ALLOWED_AVATAR_MIME_TYPES}
                        onChange={handleAvatarChange}
                        hidden
                    />
                </label>
                <span>{ALLOWED_AVATAR_EXTENSIONS.join(', ')} (max {MAX_AVATAR_SIZE/1024/1024},0 Мб )</span>
            </div>

            <input
                className="profile-input"
                type="text"
                placeholder="Название группы"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                autoFocus
                maxLength={100}
            />

            <div className="group-members">
                <div className="members-label">Участники:</div>
                <div className="selected-members">
                    {selectedMembers.length === 0 ? (
                        <span className="no-members">Пока никого не выбрали</span>
                    ) : (
                        selectedMembers.map(member => (
                            <div key={member.id} className="member-badge">
                                <span>{member.display_name || getDisplayName(member)}</span>
                                <button onClick={() => removeMember(member.id)}>
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="group-search">
                <input
                    className="profile-input"
                    type="text"
                    placeholder="Поиск пользователей..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                {searching && <div className="searching">Поиск...</div>}

                {searchResults.length > 0 && (
                    <div className="search-results">
                        {searchResults.map(user => (
                            <div 
                                key={user.id} 
                                className="search-result-item" 
                                onClick={() => addMember(user)}
                            >
                                <ChatAvatar chat={{
                                            avatar: user?.avatar_url,
                                            title:  user.display_name || user.username || 'User',
                                            chat_id: user?.id || 0,
                                            }} 
                                className="result-avatar"
                                imgClassName="avatar-img"
                                placeholderClassName="avatar-placeholder-small"
                            /> 
                                <div className="result-info">
                                    <div className="result-name">
                                        {user.display_name || user.username}
                                    </div>
                                    <div className="result-details">
                                        {user.username && <span>@{user.username}</span>}
                                        {user.phone && <span><i className="bi bi-telephone"></i> {user.phone}</span>}
                                    </div>
                                </div>
                                <i className="bi bi-plus-circle"></i>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CreateGroupModal;